import { z } from 'zod';

import { BlockValueTemplateHint, validateBlockValueTemplate } from '../../base/template.js';

export enum DynamicValueType {
  /** A JSON literal value or a non-stringified template placeholder */
  Auto = 'Auto',
  // Auto should now replace all the other options:
  /** The `data` of an upstream block, specified by name */
  Block = 'Block',
  /** The current array element within an Iterator block */
  Element = 'Element',
  /** An object path (e.g., `patient.data.firstName`) */
  Expression = 'Expression',
  /** A JSON literal value (e.g., `[true, false]`) */
  Literal = 'Literal',
  /** Templatized text (e.g., `Hello, {{patient.data.firstName}}!` */
  Template = 'Template',
}

export const DynamicValueSchema = z
  .discriminatedUnion('type', [
    z.object({
      type: z.undefined(),
    }),
    z.object({
      type: z.literal(DynamicValueType.Auto),
      jsonOrTemplate: z.string().optional(),
    }),
    z.object({
      type: z.literal(DynamicValueType.Block),
      blockName: z.string().min(1, 'Block is required'),
    }),
    z.object({
      type: z.literal(DynamicValueType.Element),
    }),
    z.object({
      type: z.literal(DynamicValueType.Expression),
      expression: z
        .string()
        .refine(
          (value) => !value.startsWith('{{'),
          'Do not use block templates like {{blockName.data}}. i.e. Remove `{{ }}` '
        ),
    }),
    z.object({
      type: z.literal(DynamicValueType.Literal),
      value: z.unknown(),
      json: z.string().optional(),
    }),
    z.object({
      type: z.literal(DynamicValueType.Template),
      template: z.string(),
    }),
  ])
  .superRefine((value, ctx) => {
    if (value.type === DynamicValueType.Auto) {
      const { jsonOrTemplate } = value;
      if (jsonOrTemplate) {
        if (jsonOrTemplate.startsWith('{{')) {
          const result = validateBlockValueTemplate(jsonOrTemplate);
          if (result.hint === BlockValueTemplateHint.NotTemplateLike) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Must end with }}',
            });
          } else if (result.hint === BlockValueTemplateHint.Invalid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: result.message,
            });
          }
        } else {
          try {
            JSON.parse(jsonOrTemplate);
          } catch (error) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
            });
          }
        }
      }
    } else if (value.type === DynamicValueType.Literal) {
      // `json` must be valid JSON and must parse to `value`
      let parsedValue: unknown = undefined;
      try {
        parsedValue = value.json ? JSON.parse(value.json) : undefined;
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
          path: ['json'],
        });
        return;
      }
      if (JSON.stringify(parsedValue) !== JSON.stringify(value.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `"value" does not match parsed value of "json"`,
        });
      }
    }
  });
export type DynamicValue = z.infer<typeof DynamicValueSchema>;
